import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, TrendingUp, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CatchCard } from "@/components/catches/CatchCard";
import { toast } from "sonner";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

const fetchUserCatches = async (userId: string | undefined) => {
  if (!userId) return [];
  
  const { data, error } = await supabase
    .from("catches")
    .select("*")
    .eq("angler_id", userId)
    .order("date_caught", { ascending: false });

  if (error) throw error;
  return data || [];
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    } else if (user) {
      trackEvent({ eventName: AnalyticsEvents.PAGE_VIEW, properties: { page: 'dashboard' } });
    }
  }, [user, authLoading, navigate]);

  const { data: catches = [], isLoading, error, refetch } = useQuery({
    queryKey: ["user-catches", user?.id],
    queryFn: () => fetchUserCatches(user?.id),
    enabled: !!user,
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
    gcTime: 3 * 60 * 1000, // Keep in cache for 3 minutes
  });

  const StatsSkeleton = () => (
    <div className="grid gap-6 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="p-6">
          <Skeleton className="h-4 w-24 mb-3" />
          <Skeleton className="h-8 w-16" />
        </Card>
      ))}
    </div>
  );

  const CatchesSkeleton = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i} className="p-6 space-y-4">
          <Skeleton className="h-48 w-full rounded" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      ))}
    </div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const totalWeight = catches.reduce((sum, c) => sum + (Number(c.weight_lbs) || 0), 0);
  const verifiedCount = catches.filter((c) => c.status === "verified").length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="container py-8 space-y-8" role="main" aria-label="Dashboard content">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gradient mb-2">My Catches</h1>
            <p className="text-muted-foreground">Track your fishing adventures</p>
          </div>
          <div className="flex gap-2" role="group" aria-label="Catch actions">
            <Button 
              size="lg" 
              className="ocean-gradient gap-2"
              onClick={() => navigate("/add-catch")}
              aria-label="Record a new catch"
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Record New Catch
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="gap-2 hidden md:flex"
              onClick={() => navigate("/bulk-add-catch")}
              aria-label="Bulk add multiple catches"
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Bulk Add
            </Button>
          </div>
        </div>

        {isLoading ? (
          <StatsSkeleton />
        ) : error ? (
          <Card className="p-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <div className="flex-1">
                <p className="font-semibold">Error loading catches</p>
                <p className="text-sm text-muted-foreground">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
              </div>
              <Button onClick={() => refetch()} variant="outline" size="sm">
                Retry
              </Button>
            </div>
          </Card>
        ) : (
          <section aria-label="Catch statistics" className="grid gap-6 md:grid-cols-3">
            <div className="p-6 rounded-xl border-2 border-primary/20 bg-card" role="article" aria-labelledby="total-catches">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-primary" aria-hidden="true" />
                <span id="total-catches" className="text-sm font-medium text-muted-foreground">Total Catches</span>
              </div>
              <p className="text-3xl font-bold" aria-label={`${catches.length} total catches`}>{catches.length}</p>
            </div>

            <div className="p-6 rounded-xl border-2 border-success/20 bg-card" role="article" aria-labelledby="verified-catches">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-success" aria-hidden="true" />
                <span id="verified-catches" className="text-sm font-medium text-muted-foreground">Verified</span>
              </div>
              <p className="text-3xl font-bold" aria-label={`${verifiedCount} verified catches`}>{verifiedCount}</p>
            </div>

            <div className="p-6 rounded-xl border-2 border-accent/20 bg-card" role="article" aria-labelledby="total-weight">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-accent" aria-hidden="true" />
                <span id="total-weight" className="text-sm font-medium text-muted-foreground">Total Weight</span>
              </div>
              <p className="text-3xl font-bold" aria-label={`${totalWeight.toFixed(1)} pounds total weight`}>{totalWeight.toFixed(1)} lbs</p>
            </div>
          </section>
        )}

        {isLoading ? (
          <CatchesSkeleton />
        ) : catches.length === 0 ? (
          <section className="text-center py-16 space-y-4" aria-label="Empty state">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto" aria-hidden="true">
              <Plus className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">No catches yet</h2>
              <p className="text-muted-foreground mb-6">Start recording your fishing adventures!</p>
              <Button onClick={() => navigate("/add-catch")} className="ocean-gradient" aria-label="Record your first catch">
                Record Your First Catch
              </Button>
            </div>
          </section>
        ) : (
          <section aria-label="Your catches" className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {catches.map((catch_) => (
              <CatchCard key={catch_.id} catch={catch_} />
            ))}
          </section>
        )}
      </main>
    </div>
  );
}